/**
 * WebPlacementPickerDialog Component
 *
 * Dialog for selecting a published WebPlacement for A/B test variants.
 * Shows only 'single' type placements that are published.
 *
 * Features:
 * - Search by name and alias
 * - Shows placement status and URL
 * - Returns selected placement for variant assignment
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Chip,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { WebPlacement } from '@modules/webplacements/types/webplacements.types';

export interface WebPlacementPickerDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Callback when a placement is selected */
  onSelect: (placement: WebPlacement) => void;
  /** Available placements to choose from (should be filtered to published single placements) */
  placements: WebPlacement[];
  /** Whether placements are loading */
  loading?: boolean;
  /** Currently selected placement ID (to highlight) */
  selectedId?: string;
  /** Dialog title */
  title?: string;
  /** IDs to exclude from selection (e.g., already used variants) */
  excludeIds?: string[];
}

/**
 * WebPlacementPickerDialog Component
 */
export const WebPlacementPickerDialog: React.FC<WebPlacementPickerDialogProps> = ({
  open,
  onClose,
  onSelect,
  placements,
  loading = false,
  selectedId,
  title = 'Select Web Placement',
  excludeIds = [],
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter placements by search query and exclude IDs
  const filteredPlacements = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const excludeSet = new Set(excludeIds);

    return placements.filter((p) => {
      // Exclude already used placements (optional)
      if (excludeSet.has(p.id)) return false;

      // Filter by search query
      if (!query) return true;

      return (
        p.name.toLowerCase().includes(query) ||
        p.alias.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
      );
    });
  }, [placements, searchQuery, excludeIds]);

  // Handle selection
  const handleSelect = (placement: WebPlacement) => {
    onSelect(placement);
    onClose();
    setSearchQuery(''); // Reset search on close
  };

  // Handle close
  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-testid="webabt-picker-dialog">
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        {/* Search Field */}
        <TextField
          fullWidth
          data-testid="webabt-picker-search"
          placeholder="Search by name or alias..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          autoFocus
        />

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* No Placements Available */}
        {!loading && placements.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No published web placements available. Create and publish at least 2 web placements first.
          </Alert>
        )}

        {/* No Results */}
        {!loading && placements.length > 0 && filteredPlacements.length === 0 && (
          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
            No placements match "{searchQuery}"
          </Typography>
        )}

        {/* Placements List */}
        {!loading && filteredPlacements.length > 0 && (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {filteredPlacements.map((placement) => (
              <ListItemButton
                key={placement.id}
                data-testid={`webabt-picker-alias-${placement.alias}`}
                onClick={() => handleSelect(placement)}
                selected={placement.id === selectedId}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  border: '1px solid',
                  borderColor: placement.id === selectedId ? 'primary.main' : 'divider',
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {placement.name}
                      </Typography>
                      {placement.id === selectedId && (
                        <CheckCircleIcon color="primary" fontSize="small" />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip
                        label={`/${placement.alias}`}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                      {placement.status === 'published' && (
                        <Chip
                          label="Published"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                      {placement.config?.funnelName && (
                        <Typography variant="caption" color="text.secondary">
                          {placement.config.funnelName}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WebPlacementPickerDialog;
